import capitalize from 'lodash/capitalize'
import compact from 'lodash/compact'
import fromPairs from 'lodash/fromPairs'
import lowerCase from 'lodash/lowerCase'

import { AccountTableSchemaQuery } from 'graphql/bindings/AdminBindings'
import { makeConditionParser, makeMultiConditionParser } from 'util/grammar'

export interface BaseField {
  name: string
  simpleName: string
  isNullable: boolean
}

export type EnumField = BaseField & { type: 'enum'; options: string[]; isMulti: boolean }
export type StringField = BaseField & { type: 'string' }
export type NumberField = BaseField & { type: 'number' }
export type BooleanField = BaseField & { type: 'boolean' }
export type DateTimeField = BaseField & { type: 'datetime' }
export type DurationField = BaseField & { type: 'duration' }

export type Field = EnumField | StringField | NumberField | BooleanField | DateTimeField | DurationField

export type FieldMap = { [key: string]: Field }

export const parseField: (jsonString: string) => Field = (jsonString: string) => {
  const parsedObj = JSON.parse(jsonString)
  const topLevelKey = Object.keys(parsedObj)[0]
  const subObject = parsedObj[topLevelKey]
  switch (topLevelKey) {
    case 'EnumField':
      return { ...subObject, type: 'enum' }
    case 'NumberField':
      return { ...subObject, type: 'number' }
    case 'BooleanField':
      return { ...subObject, type: 'boolean' }
    case 'DateTimeField':
      return { ...subObject, type: 'datetime' }
    case 'DurationField':
      return { ...subObject, type: 'duration' }
    default:
      return { ...subObject, type: 'string' }
  }
}

interface MultiCondition {
  include: string[]
  exclude: string[]
}

export const parseTotalCondition = (value: string) => {
  try {
    const parser = makeMultiConditionParser()
    parser.feed(value)
    const results = parser.results
    if (results && !results.length) {
      return { include: [], exclude: [] }
    }
    const result: MultiCondition = results[0]
    return result
  } catch (err) {
    return { include: [], exclude: [] }
  }
}

export const prettifyName = (name: string) =>
  name
    .split('-')
    .map(sub =>
      sub
        .split('+')
        .map(substring => capitalize(lowerCase(substring)))
        .join('+')
    )
    .join('-')

export const numericStandardOps = [
  { label: '>', value: '>' },
  { label: '<', value: '<' },
  { label: '≥', value: 'ge' },
  { label: '≤', value: 'le' },
]
export const stringOps = [
  { label: 'Contains', value: 'contains' },
  { label: '=', value: '=' },
]
export const numberOps = [...numericStandardOps, { label: '=', value: '=' }]
export const enumOps = [{ label: 'In', value: 'in' }]
export const booleanOps = [{ label: '=', value: '=' }]
export const dateTimeOps = [
  { label: 'After', value: '>' },
  { label: 'Before', value: '<' },
]
export const durationOps = numericStandardOps

export const opsLookup = {
  string: stringOps,
  number: numberOps,
  enum: enumOps,
  boolean: booleanOps,
  datetime: dateTimeOps,
  duration: durationOps,
}

const fieldsWhitelist = [
  'crm.account.type',
  'crm.account.is_investor',
  'crm.account.is_shareholder',
  'crm.account.has_done_secondaries_before',
  'crm.account.has_done_primaries_before',
  'crm.account.net_worth',
  'crm.account.aum',
  'crm.account.source',
  'crm.account.created_at',
  'crm.account.aum_secondaries',
  'crm.account.relationship_strength',
  'crm.account.relationship_sensitivity',
  'crm.account.is_account_of_interest',
  'crm.account.has_private_market_experience',
  'crm.account.qualified',
  'crm.account.business_development_tier',
  'crm.investor_account_preferences.investment_structure_comfort',
  'crm.investor_account_preferences.only_profitable_companies',
  'crm.investor_account_preferences.opportunities_of_interest',
  'crm.investor_account_preferences.target_check_size',
  'crm.investor_account_preferences.has_cash_on_hand',
  'crm.investor_account_preferences.company_stages',
  'crm.investor_account_preferences.target_regions',
  'computed.accounts.account_level',
  'computed.accounts.num_active_deals',
  'computed.accounts.num_closed_deals',
  'computed.accounts.country',
  'computed.accounts.state',
  'computed.accounts.city',
  'computed.accounts.do_not_contact_reasons',
  'computed.accounts.last_transaction_closed_date',
  'computed.accounts.last_opportunity_created_date',
  'computed.accounts.last_outreach_by_anyone_date',
  'computed.accounts.last_inbound_interaction_date',
  'computed.accounts.num_introductions',
  'computed.accounts.owners',
  'computed.accounts.has_transacted',
  'computed.accounts.name',
  'computed.accounts.email',
  'computed.accounts.sectors_of_interest',
  'computed.accounts.has_done_secondaries',
  'computed.accounts.average_order_size',
  'computed.accounts.last_order_created_date',
  'computed.accounts.last_order_closed_date',
  'computed.accounts.num_closed_orders',
  'computed.accounts.num_active_orders',
  'computed.accounts.order_structures',
  'computed.accounts.companies_of_interest',
  'computed.accounts.purchased_companies',
  'computed.accounts.holdings',
  'computed.accounts.region',
  'computed.accounts.client_type',
  'computed.accounts.is_accredited',
]

export const fieldMapFromQuery = (data?: AccountTableSchemaQuery) => {
  const fields = (data?.accountsTableSchema.fields ?? []).map((fld: any) => parseField(fld))
  const fieldPairs = fields
    .filter(fld => fieldsWhitelist.includes(fld.name))
    .map(fld => {
      return [fld.name, fld]
    })
  const fieldsLookup: FieldMap = fromPairs(fieldPairs)
  return fieldsLookup
}

export const optionsForField = (fld: Field) => opsLookup[fld.type]

export interface ConditionStateType {
  name: string | null
  operation: string | null
  value: null | string | string[]
}

export const validateField = (state: ConditionStateType, field: Field) => {
  if (state.value === null) {
    return false
  }
  const operationOptions = optionsForField(field)
  if (!operationOptions.some(op => op.value === state.operation)) {
    return false
  }
  if (field.type === 'enum') {
    if (Array.isArray(state.value)) {
      if (state.operation !== 'in' && state.operation !== 'not in') {
        return false
      }
      const containsNull = state.value.some(e => e === 'null')
      const valuesWithoutNull = state.value.filter(e => e !== 'null')
      if (!valuesWithoutNull.every(e => field.options.includes(e))) {
        return false
      }
      if (containsNull && !field.isNullable) return null
    }
  }
  return true
}

export const booleanOptions = [
  { label: 'True', value: 'true' },
  { label: 'False', value: 'false' },
]

export interface ParseResult {
  name: string
  op: string
  value: string[]
}

const DEFAULT_CONDITION_STATE = { name: null, operation: null, value: null }

// Value may be a string for a single query clause that takes the form ""
export const convertValueToConditionState = (value: string, fields: FieldMap) => {
  if (value === '') {
    return { name: null, operation: null, value: null }
  }
  try {
    const parser = makeConditionParser()
    parser.feed(value)
    const results = parser.results

    if (results && !results.length) return DEFAULT_CONDITION_STATE

    const result: ParseResult = results[0]
    const currentField = fields[result.name]

    if (!currentField) return DEFAULT_CONDITION_STATE

    const nameOut = { label: prettifyName(currentField.simpleName), value: currentField.name }
    const availableOps = optionsForField(currentField)
    const currentOp =
      availableOps.length === 1 ? availableOps[0] : availableOps.find(op => op.value === result.op) || null
    const baseValueOptions =
      currentField?.type === 'boolean'
        ? booleanOptions
        : (currentField && currentField.type === 'enum' ? currentField.options : []).map(e => ({
            label: prettifyName(e),
            value: e,
          }))
    const isNullable = !!currentField?.isNullable
    const valueOptions = compact([...baseValueOptions, isNullable && { label: 'Blank', value: 'null' }])
    if (currentOp?.value === 'in' || currentOp?.value === 'not in') {
      return { name: nameOut.value, operation: currentOp.value, value: result.value }
    }
    const singleValue = result.value[0]
    const possibleValue = valueOptions.find(e => e.value === singleValue)
    return {
      name: nameOut.value,
      operation: currentOp?.value || null,
      value: possibleValue ? possibleValue.value : singleValue,
    }
  } catch (err) {
    console.error(err)
    return { name: null, operation: null, value: null }
  }
}
